import React, { useMemo } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";
import "../App.css";
import useFeaturedCategories from "../hooks/useFeaturedCategories";
import useImagePrefetch, { prefetchImages } from "../hooks/useImagePrefetch";
import ProductCardSkeleton from "./ProductCardSkeleton";
import { useQueryClient } from "@tanstack/react-query";
import { qk } from "../api/queryKeys";
import { getProducts } from "../api/endpoints";

const CATEGORY_DISPLAY_ORDER = [
  { label: "Table Linen & Overlays", slugs: ["table-linen-overlays"] },
  { label: "Table Runners", slugs: ["table-runners", "table-runner"] },
  { label: "Napkins", slugs: ["napkins"] },
  { label: "Chargers & Placemats", slugs: ["chargers-placemats"] },
  { label: "Chair Accents", slugs: ["chair-accents"] },
  { label: "Spandex", slugs: ["spandex", "spandex-products"] },
  { label: "Accessories", slugs: ["accessories"] },
];

const decodeHtmlEntities = (text) => {
  if (!text) return "";
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

const FeaturedCategories = ({ onSelectCategory }) => {
  const { data: categories = [], isLoading } = useFeaturedCategories();

  const orderedCategories = useMemo(
    () =>
      CATEGORY_DISPLAY_ORDER.map((rule) => {
        const match = categories.find((c) => rule.slugs.includes(c?.slug));
        if (!match) return null;
        return { ...match, name: rule.label };
      }).filter(Boolean),
    [categories]
  );

  useImagePrefetch(orderedCategories.map((c) => c.image).filter(Boolean));

  const queryClient = useQueryClient();

  const handleCategoryHover = (slug) => {
    if (!slug) return;
    queryClient
      .prefetchInfiniteQuery({
        queryKey: qk.products.list(slug, {}),
        queryFn: ({ pageParam = 1, signal }) =>
          getProducts({
            category: slug,
            filters: {},
            page: pageParam,
            signal,
          }),
        initialPageParam: 1,
        staleTime: 5 * 60 * 1000,
      })
      .then(() => {
        const cached = queryClient.getQueryData(qk.products.list(slug, {}));
        const firstPage = cached?.pages?.[0];
        const urls = (firstPage?.products || [])
          .map((p) => p?.featured_image)
          .filter(Boolean);
        prefetchImages(urls);
      })
      .catch(() => {});
  };

  const handleCategoryClick = (link) => {
    if (onSelectCategory) onSelectCategory(link);
  };

  return (
    <Box sx={{ mb: 4 }}>
      {isLoading ? (
        <Grid container spacing={3}>
          {CATEGORY_DISPLAY_ORDER.map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={`cat-skel-${index}`}>
              <ProductCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {orderedCategories.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Link
                to={`/?key=${item.slug}`}
                onClick={() => handleCategoryClick(item.slug)}
                onMouseEnter={() => handleCategoryHover(item.slug)}
                onFocus={() => handleCategoryHover(item.slug)}
                onTouchStart={() => handleCategoryHover(item.slug)}
                style={{ textDecoration: "none", color: "inherit" }}>
                <Box
                  sx={{
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    maxWidth: "300px",
                    margin: "0 auto",
                  }}>
                  <Box
                    sx={{
                      width: "100%",
                      maxHeight: "220px",
                      overflow: "hidden",
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      position: "relative",
                      "&:hover .overlay": { opacity: 1 },
                    }}>
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        decoding="async"
                        loading="eager"
                        style={{
                          width: "100%",
                          height: "220px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: "#999" }}>
                        No image available
                      </Typography>
                    )}
                    <Box
                      className="overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "#333333",
                          color: "#fff",
                          padding: "12px 20px",
                          fontSize: "14px",
                          fontWeight: 400,
                          borderRadius: "0px",
                          textTransform: "none",
                          "&:hover": { backgroundColor: "#333" },
                        }}>
                        View Products
                      </Button>
                    </Box>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "18px",
                      lineHeight: "27px",
                      color: "#000000",
                      letterSpacing: "0%",
                      fontWeight: 500,
                      mt: 2,
                      fontFamily: "Avenir Book",
                      textAlign: "left",
                    }}>
                    {decodeHtmlEntities(item.name)}
                  </Typography>
                </Box>
              </Link>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default FeaturedCategories;
